import { request } from "@umijs/max";
import type { User } from "./typings";

const USER_STORAGE_KEY = "currentUser";
const TOKEN_STORAGE_KEY = "token";
const memoryStorage = new Map<string, string>();

const getStorage = () => {
  if (typeof localStorage !== "undefined") {
    return localStorage;
  }

  return {
    getItem: (key: string) => memoryStorage.get(key) ?? null,
    setItem: (key: string, value: string) => memoryStorage.set(key, value),
    removeItem: (key: string) => memoryStorage.delete(key),
  };
};

interface Envelope<T> {
  code?: number;
  message?: string;
  data?: T;
}

const unwrap = <T>(payload: T | Envelope<T>): T => {
  if (
    payload &&
    typeof payload === "object" &&
    "data" in payload &&
    "code" in payload
  ) {
    return (payload as Envelope<T>).data as T;
  }
  return payload as T;
};

const parseStoredUser = () => {
  const storage = getStorage();
  const storedUser = storage.getItem(USER_STORAGE_KEY);
  if (!storedUser) {
    return null;
  }

  try {
    return JSON.parse(storedUser) as User.UserItem;
  } catch (error) {
    // 如果解析失败，清理掉损坏的数据
    storage.removeItem(USER_STORAGE_KEY);
    console.error("Failed to parse stored user:", error);
    return null;
  }
};

export const persistCurrentUser = (user: User.UserItem) => {
  getStorage().setItem(USER_STORAGE_KEY, JSON.stringify(user));
};

export const clearCurrentUser = () => {
  getStorage().removeItem(USER_STORAGE_KEY);
};

export const persistAuthToken = (token: string) => {
  getStorage().setItem(TOKEN_STORAGE_KEY, token);
};

export const getAuthToken = () => getStorage().getItem(TOKEN_STORAGE_KEY);

export const clearAuthToken = () => {
  getStorage().removeItem(TOKEN_STORAGE_KEY);
};

/** 用户登录 POST /api/v1/auth/login */
export async function loginUser(
  data: User.UserLoginParams,
  options?: { [key: string]: any }
) {
  const res = await request<
    User.UserLoginResponse["data"] | Envelope<User.UserLoginResponse["data"]>
  >("/api/v1/auth/login", {
    method: "POST",
    data,
    ...(options || {}),
  });
  return unwrap<User.UserLoginResponse["data"]>(res);
}

export async function fetchUserRoles(
  params?: { [key: string]: any },
  options?: { [key: string]: any }
) {
  const res = await request<
    User.UserRoleListResponse | Envelope<User.UserRoleListResponse>
  >("/api/v1/users/roles", {
    method: "GET",
    params,
    ...(options || {}),
  });
  return unwrap<User.UserRoleListResponse>(res);
}

export async function getCurrentUser(
  options?: { [key: string]: any }
): Promise<User.UserItem> {
  const user = parseStoredUser();

  if (!user) {
    const error: any = new Error("用户信息不存在，请重新登录");
    if (options?.skipErrorHandler) {
      error.skipErrorHandler = true;
    }
    throw error;
  }

  return user;
}
